import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {msg} from '@lit/localize';
import superagent from 'superagent';

import store from '../../../store';
import {conditional, conditionalWithConditions} from '../../../util/conditional';
import {getProductData} from '../../../util/getProductData';
import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {selectLePolar, selectPolar, selectSpcConfiguration, selectSpcConfigurations} from '../../../selectors';

@customElement('olivio-specsheet-head-light-page')
export class OlivioSpecsheetHeadLightPage extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
			}

			.pageContainer {
				display: flex;
				flex-direction: column;
				justify-content: space-between;
				width: 100%;
				gap: 1cqw;
				padding-top: 2cqw;
				container-type: inline-size;
			}

			.colums {
				display: flex;
				flex-direction: row;
			}
			.text {
				width: 40%;
				font: var(--font-specsheet-print-small-text);
				font-size: 1.4cqw;
			}
			.diagram {
				width: 60%;
			}
			.llmfTable {
				width: 100%;
				container-type: inline-size;
				padding-top: 1cqw;
			}

			.polarImage {
				width: 100%;
			}

			.heads {
				font: var(--font-specsheet-print-small-text);
				font-size: 1.2cqw;
				display: flex;
				flex-direction: row;
				gap: 1cqw;
				align-items: center;
			}
			.head {
				display: flex;
				justify-content: center;
				align-items: center;
				background-color: black;
				color: white;
				height: 2cqw;
				width: 2cqw;
				border-radius: 1cqw;
			}
			.tableTitle {
				font: var(--font-specsheet-print-small-bold);
				font-size: 1.4cqw;
			}
			table {
				border-collapse: collapse;
				width: 100%;
				border-bottom: 1px solid rgb(198, 198, 198);
				border-top: 1px solid rgb(198, 198, 198);
				margin-top: 1cqw;
				margin-bottom: 1cqw;
			}

			tr {
				border-bottom: 1px solid rgb(198, 198, 198);
			}

			tr:last-child {
				border-bottom: none;
			}

			th {
				font: var(--font-specsheet-print-small-bold);
				font-size: 1.4cqw;
				text-align: left;
			}

			td {
				font: var(--font-specsheet-print-small-text);
				font-size: 1.4cqw;
				padding: 1cqw 1cqw;
			}
		`]
	;

	@property({type: Object})
	headData = null;

	@property({type: Object})
	llmf = null;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
		this.loadedLlmfJson = null;
		this.connected = false;
	}
	connectedCallback() {
		super.connectedCallback();
		this.connected = true;
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.connected = false;
	}
	stateChanged(state) {
		this.state = state;
		this.productData = state.productData;
		this.status = state.status;
		this.lang = state.status.lang;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.housingSize = this.headData.head.housingSize;
		this.lightDistribution = this.headData.head.lightDistribution;
		this.colourTemperature = this.headData.head.colourTemperature;

		const conditions = [
			'HS_HousingSize.' + this.housingSize,
			'LE_ColourTemperature_1.' + this.colourTemperature,
			'LE_LightDistribution_1.' + this.lightDistribution,
			'LE_LightColour.' + this.colourTemperature
		];
		this.polarImage = conditionalWithConditions(conditions, this.conditionalVariantValues, 'RETURN_LE_PolarDiagramP2', 'EditorialTable', 1);
		this.llmfJson = conditionalWithConditions(conditions, this.conditionalVariantValues, 'RETURN_LE_LLMF_JSON', 'EditorialTable', 1);
		this.lightEngineDatasetNumber = conditionalWithConditions(conditions, this.conditionalVariantValues, 'LE_LightEngineDataSetNumber', 'EditorialTable', 1);
		this.ulor = conditionalWithConditions(conditions, this.conditionalVariantValues, 'LU_ULOR', 'EditorialTable', 1);

		// get the LDT file, when connected
		if (this.connected) {
			const spcConfig = selectSpcConfigurations(this.state)[this.headData.instanceId];
			const reflectorColour = getValueOfFeature('root::LE_ReflectorColour_1', spcConfig);
			const dimmingLevel = getValueOfFeature('root::LE_DimmingLevel_1', spcConfig);
			const accessory = getValueOfFeature('root::LE_Accessories_1', spcConfig);

			const ldtConditions = [
				'HS_HousingSize.' + this.housingSize,
				'LE_ColourTemperature_1.' + this.colourTemperature,
				'LE_LightDistribution_1.' + this.lightDistribution,
				'LE_ReflectorColour_1.' + reflectorColour,
				'LE_DimmingLevel_1.' + dimmingLevel,
				'LE_Accessories_1.' + accessory
			];
			this.ldtfile = conditionalWithConditions(ldtConditions, this.conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1);

			// dynamically load the file with url in variable this.llmfJson into this.llmf
			// do not put into redux state, treat it as a static asset to display on the page
			if (this.llmfJson && this.loadedLlmfJson !== this.llmfJson) {
				this.loadedLlmfJson = this.llmfJson;
				console.log('Loading LLMF data from:', this.llmfJson);
				superagent
					.get(this.llmfJson)
					.then(response => {
						this.llmf = response.body; // superagent automatically parses JSON responses
					})
					.catch(error => {
						console.error('Error loading LLMF data:', error);
						this.llmf = null;
					});
			}
		}
	}


	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id, 'EditorialTable', 1);
	}

	render() {
		return html`
			<div class="heads">${msg('Heads')}
				${repeat(this.headData.indexes, (i) => i, (i) => html`
					<span class="head">${i + 1}</span>
				`)}
			</div>
			<div class="pageContainer">

				<div class="colums">
					<div class="text">
						${when(this.lightEngineDatasetNumber, () => html`<trans-label key="LE_LightEngineDataset" context="D"></trans-label>: ${this.lightEngineDatasetNumber}<br/>`)}
						${when(this.ulor, () => html`<trans-label key="LU_ULOR" context="D.L"></trans-label>: ${this.ulor} %<br/>`)}
						<br>
						<trans-label key="LE_ColourTemperature_1" context="F"></trans-label><br>
						<trans-label slot="value" instanceId="${this.headData.instanceId}" key="LE_ColourTemperature_1" value="${this.headData.head.colourTemperature}" context="FV.S"></trans-label><br>
						<br>
						<trans-label key="LE_LightDistribution_1" context="F"></trans-label><br>
						<trans-label slot="value" instanceId="${this.headData.instanceId}" key="LE_LightDistribution_1" value="${this.headData.head.lightDistribution}" context="FV.S"></trans-label><br>
						<br>
						${when(this.ldtfile, () => html`LDT: <br><a href="${this.ldtfile}" target="_blank">${msg('Download')}</a><br>`)}
					</div>
					<div class="diagram">
						${when(this.polarImage !== '', () => html`
							<img class="polarImage" alt="product image" src="${this.polarImage}"/>
						`)}
					</div>
				</div>
				<div class="llmfTable">
					<div class="tableTitle"><trans-label class="title" key="LE_LuminousFlux_OperatingTime" context="D"></trans-label></div>
					${when(this.llmf !== null, () => html`
						<table>
							<tr>
								<th>
									<trans-label key="LE_OperatingTimeLED" context="D"></trans-label>
								</th>
								${repeat(this.llmf.llmftable, (item) => (
									html`
										<td>${item.ophours}</td>`
								))}
							</tr>
							<tr>
								<th>
									<trans-label key="LE_Lumenmaintenance" context="D"></trans-label>
								</th>
								${repeat(this.llmf.llmftable, (item) => (
									html`
										<td>${item.factor * 100}%</td>`
								))}
							</tr>
							<tr>
								<th>
									<trans-label key="LE_LLMF" context="D"></trans-label>
									*
								</th>
								${repeat(this.llmf.llmftable, (item) => (
									html`
										<td>${item.factor}</td>`
								))}
							</tr>
						</table>
					`)}
				</div>
			</div>
		`;
	}
}
